import { useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Play, Square } from 'lucide-react';

const SPEEDS = [0.75, 1, 1.25, 1.5];

export default function ArticleTTS({ title, body, lang }) {
  const [supported, setSupported] = useState(false);
  const [state, setState] = useState('idle'); // idle | playing | paused
  const [speed, setSpeed] = useState(1);
  const utterRef = useRef(null);

  useEffect(() => {
    setSupported(typeof window !== 'undefined' && 'speechSynthesis' in window);
    return () => { try { window.speechSynthesis?.cancel(); } catch {} };
  }, []);

  function pickVoice() {
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      const target = (lang || 'hr').toLowerCase();
      const exact = voices.find(v => v.lang?.toLowerCase().startsWith(`${target}-`));
      if (exact) return exact;
      const anyHr = voices.find(v => v.lang?.toLowerCase().startsWith('hr'));
      if (anyHr) return anyHr;
      return voices[0];
    } catch { return null; }
  }

  function plain(html) {
    return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function start() {
    try {
      const text = `${title}. ${plain(body)}`;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = (lang === 'hr' ? 'hr-HR' : lang);
      u.rate = speed;
      const v = pickVoice();
      if (v) u.voice = v;
      u.onend = () => setState('idle');
      u.onerror = () => setState('idle');
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
      utterRef.current = u;
      setState('playing');
    } catch {
      setState('idle');
    }
  }

  function pause() { window.speechSynthesis.pause(); setState('paused'); }
  function resume() { window.speechSynthesis.resume(); setState('playing'); }
  function stop() { window.speechSynthesis.cancel(); setState('idle'); }

  function changeSpeed(s) {
    setSpeed(s);
    if (state === 'playing' || state === 'paused') { stop(); setTimeout(() => start(), 40); }
  }

  if (!supported) return null;

  return (
    <div className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-1" data-testid="article-tts">
      {state === 'idle' && (
        <button onClick={start} className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:text-red-600" data-testid="tts-play">
          <Volume2 size={13} /> Slušaj članak
        </button>
      )}
      {state === 'playing' && (
        <>
          <button onClick={pause} className="p-1 text-gray-700 dark:text-gray-200 hover:text-red-600" title="Pauza" data-testid="tts-pause"><Pause size={14} /></button>
          <button onClick={stop} className="p-1 text-gray-700 dark:text-gray-200 hover:text-red-600" title="Stop" data-testid="tts-stop"><Square size={14} /></button>
        </>
      )}
      {state === 'paused' && (
        <>
          <button onClick={resume} className="p-1 text-gray-700 dark:text-gray-200 hover:text-red-600" title="Nastavi" data-testid="tts-resume"><Play size={14} /></button>
          <button onClick={stop} className="p-1 text-gray-700 dark:text-gray-200 hover:text-red-600" title="Stop" data-testid="tts-stop"><Square size={14} /></button>
        </>
      )}
      <div className="flex items-center gap-0.5 pl-1 border-l border-gray-300 dark:border-gray-700 ml-1">
        {SPEEDS.map(s => (
          <button key={s} onClick={() => changeSpeed(s)}
            className={`text-[10px] px-1.5 py-0.5 rounded ${speed === s ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:text-red-600'}`}
            data-testid={`tts-speed-${s}`}>
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}
